import { Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import { ErrorComponent, useNotificationProvider, } from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import routerBindings, { NavigateToResource, UnsavedChangesNotifier, } from "@refinedev/react-router-v6";
import { App as AntdApp, ConfigProvider } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { CustomLayout } from "./components/Layout";
import dataProvider from "./providers/data";
import { customDataProvider } from "./providers/customDataProvider";
import { InventoryCreate, InventoryEdit, InventoryList, InventoryShow } from "./pages/inventory";
import { ProductCreate, ProductEdit, ProductList, ProductShow } from "./pages/products";
import { StockList, StockAlerts, StockAdjustments } from "./pages/stock";
import { PurchaseCreate, PurchaseList, PurchaseShow } from "./pages/purchases";
import { SaleCreate, SaleEdit, SaleList, SaleShow } from "./pages/sales";
import { UserCreate, UserEdit, UserList, } from "./pages/users";
import { SaleOrderCreate, SaleOrderEdit, SaleOrderList, SaleOrderShow } from "./pages/sale-orders";
import { PurchaseOrderCreate, PurchaseOrderList, PurchaseOrderShow } from "./pages/purchaseorders";
import { CustomerCreate, CustomerEdit, CustomerList, CustomerShow } from "./pages/customers";
import ProtectedRoute from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CurrencyCreate, CurrencyEdit, CurrencyList, CurrencyShow } from "./pages/currency";
import { CompanyList, CompanyShow, CompanyEdit } from "./pages/company";
import { VendorCreate, VendorEdit, VendorList, VendorShow } from "./pages/vendors";
import { QuotationCreate, QuotationEdit, QuotationList, QuotationShow } from "./pages/quotations";
import { Reports } from "./pages/reports/Reports";
import { Dashboard } from "./pages/dashboard";
import {
    AppstoreOutlined,
    BarChartOutlined,
    BankOutlined,
    DashboardOutlined,
    DollarOutlined,
    ShopOutlined,
    ShoppingOutlined,
    TagOutlined,
    TeamOutlined,
    FileTextOutlined,
    CalculatorOutlined
} from "@ant-design/icons";
import { SaleReturnCreate, SaleReturnList, SaleReturnShow } from "./pages/sale-returns";
import { PriceListCreate, PriceListEdit, PriceListList, PriceListShow } from "./pages/prices";
import { AuthProvider } from "./contexts/auth-context";
import Login from "./components/Login";
import Logout from "./components/Logout";
import { LogoutOutlined } from "@ant-design/icons";
import { BranchList, BranchCreate, BranchEdit, BranchShow } from './pages/branch';
import { TaxesCreate } from "./pages/taxes/create";
import { TaxesEdit, TaxesList, TaxesShow } from "./pages/taxes";
import { FiscalisationDashboard } from "./pages/fiscalisation";
import { ChartOfAccountsList, ChartOfAccountsCreate, ChartOfAccountsShow, JournalEntriesList, JournalEntriesCreate, JournalEntriesShow, FinancialReports, VatReport, AccountingPeriodsList, AccountingPeriodsCreate, AccountingPeriodsEdit, AccountingPeriodsShow, PeriodBalancesList, PeriodBalancesShow } from "./pages/accounting";


function App() {
    const basename = '/';
    console.log('Using basename:', basename);

    return (
        <BrowserRouter basename={basename}>
            <AuthProvider>
                <RefineKbarProvider>
                    <ConfigProvider
                        theme={{
                            token: {
                                colorPrimary: '#3b82f6',
                                colorSuccess: '#10b981',
                                colorWarning: '#f59e0b',
                                colorError: '#ef4444',
                                colorInfo: '#3b82f6',
                                borderRadius: 10,
                                fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            },
                            components: {
                                Card: {
                                    borderRadiusLG: 14,
                                    boxShadowTertiary: '0 2px 8px rgba(0, 0, 0, 0.06)',
                                },
                                Button: {
                                    borderRadius: 10,
                                    controlHeight: 40,
                                },
                                Input: {
                                    borderRadius: 10,
                                    controlHeight: 40,
                                },
                                Select: {
                                    borderRadius: 10,
                                    controlHeight: 40,
                                },
                                Table: {
                                    borderRadius: 14,
                                },
                                Menu: {
                                    itemBorderRadius: 10,
                                    subMenuItemBorderRadius: 10,
                                },
                            },
                        }}
                    >
                        <AntdApp>
                            <ErrorBoundary>
                                <Refine
                                    options={{
                                        syncWithLocation: true,
                                        warnWhenUnsavedChanges: true,
                                        title: {
                                            text: "FrontDesk",
                                        },
                                    }}
                                    dataProvider={{
                                        default: dataProvider,
                                        users: customDataProvider
                                    }}
                                    notificationProvider={useNotificationProvider}
                                    routerProvider={routerBindings}
                                    resources={[
                                        {
                                            name: "dashboard",
                                            list: "/dashboard",
                                            meta: {
                                                icon: <DashboardOutlined />,
                                                label: "Dashboard"
                                            }
                                        },
                                        {
                                            name: "currency",
                                            meta: {
                                                label: "Currencies",
                                                icon: <DollarOutlined />
                                            },
                                            list: "/currency",
                                            create: "/currency/create",
                                            edit: "/currency/edit/:id",
                                            show: "/currency/show/:id",
                                        },
                                        {
                                            name: "taxes",
                                            meta: {
                                                label: "Taxes",
                                                icon: <TagOutlined />
                                            },
                                            list: "/taxes",
                                            create: "/taxes/create",
                                            edit: "/taxes/edit/:id",
                                            show: "/taxes/show/:id",
                                        },
                                        {
                                            name: "company",
                                            meta: {
                                                label: "Company",
                                                icon: <BankOutlined />
                                            },
                                            list: "/company",
                                            edit: "/company/edit/:id",
                                            show: "/company/show/:id",
                                        },
                                        {
                                            name: "customers",
                                            meta: {
                                                label: "Customers",
                                                icon: <TeamOutlined />
                                            },
                                            list: "/customers",
                                            create: "/customers/create",
                                            edit: "/customers/edit/:id",
                                            show: "/customers/show/:id",
                                        },
                                        {
                                            name: "vendors",
                                            meta: {
                                                label: "Vendors",
                                                icon: <TeamOutlined />
                                            },
                                            list: "/vendors",
                                            create: "/vendors/create",
                                            edit: "/vendors/edit/:id",
                                            show: "/vendors/show/:id",
                                        },
                                        {
                                            name: "Product",
                                            list: "/products",
                                            create: "/products/create",
                                            edit: "/products/edit/:id",
                                            show: "/products/show/:id",
                                            meta: {
                                                label: "Products",
                                                icon: <AppstoreOutlined />,
                                                canDelete: true,
                                            },
                                        },
                                        {
                                            name: "stock",
                                            list: "/stock",
                                            meta: {
                                                label: "Stock Management",
                                                icon: <AppstoreOutlined />,
                                            },
                                        },
                                        {
                                            name: "stock-alerts",
                                            list: "/stock/alerts",
                                            meta: {
                                                label: "Stock Alerts",
                                                icon: <AppstoreOutlined />,
                                                parent: "stock"
                                            },
                                        },
                                        {
                                            name: "stock-adjustments",
                                            list: "/stock/adjustments",
                                            meta: {
                                                label: "Adjustment history",
                                                icon: <AppstoreOutlined />,
                                                parent: "stock"
                                            },
                                        },
                                        {
                                            name: "inventory",
                                            list: "/inventory",
                                            create: "/inventory/create",
                                            edit: "/inventory/edit/:id",
                                            show: "/inventory/show/:id",
                                            meta: {
                                                label: "Legacy Inventory",
                                                icon: <AppstoreOutlined />,
                                                canDelete: true,
                                                hide: true,
                                            },
                                        },
                                        {
                                            name: "purchase",
                                            meta: {
                                                label: "Purchases Management",
                                                icon: <ShoppingOutlined />
                                            }
                                        },
                                        {
                                            name: "purchaseorders",
                                            list: "/purchaseorders",
                                            create: "/purchaseorders/create",
                                            show: "/purchaseorders/show/:id",
                                            meta: {
                                                label: "Purchase Orders",
                                                icon: <ShoppingOutlined />,
                                                parent: "purchase"
                                            }
                                        },
                                        {
                                            name: "purchases",
                                            list: "/purchases",
                                            create: "/purchases/create",
                                            edit: "/purchases/edit/:id",
                                            show: "/purchases/show/:id",
                                            meta: {
                                                label: "Purchases",
                                                icon: <ShoppingOutlined />,
                                                parent: "purchase",
                                                canDelete: true,
                                            },
                                        },
                                        {
                                            name: "sales-management",
                                            meta: {
                                                label: "Sales Management",
                                                icon: <ShopOutlined />
                                            }
                                        },
                                        {
                                            name: "sales",
                                            meta: {
                                                label: "Sales",
                                                icon: <ShoppingOutlined />
                                            },
                                            list: "/sales",
                                            create: "/sales/create",
                                            edit: "/sales/edit/:id",
                                            show: "/sales/show/:id",
                                        },
                                        
                                        {
                                            name: "saleorders",
                                            meta: {
                                                label: "Proforma Invoices",
                                                icon: <ShopOutlined />,
                                                parent: "sales-management"
                                            },
                                            list: "/sale-orders",
                                            create: "/sale-orders/create",
                                            edit: "/sale-orders/edit/:id",
                                            show: "/sale-orders/show/:id",
                                        },
                                        {
                                            name: "salereturns",
                                            meta: {
                                                label: "Sale Returns",
                                                icon: <ShopOutlined />,
                                                parent: "sales-management"
                                            },
                                            list: "/salereturns",
                                            create: "/salereturns/create",
                                            show: "/salereturns/show/:id",
                                        },
                                        {
                                            name: "quotations",
                                            meta: {
                                                label: "Quotations",
                                                icon: <ShopOutlined />,
                                                parent: "sales-management"
                                            },
                                            list: "/quotations",
                                            create: "/quotations/create",
                                            edit: "/quotations/edit/:id",
                                            show: "/quotations/show/:id",
                                        },
                                        {
                                            name: "users",
                                            meta: {
                                                label: "Users",
                                                icon: <TeamOutlined />,
                                                dataProviderName: "keycloak",
                                            },
                                            list: "/users",
                                            create: "/users/create",
                                            edit: "/users/edit/:id",
                                        },
                                        {
                                            name: "accounting",
                                            meta: {
                                                label: "Accounting",
                                                icon: <CalculatorOutlined />
                                            }
                                        },
                                        {
                                            name: "chartofaccounts",
                                            meta: {
                                                label: "Chart of Accounts",
                                                icon: <BankOutlined />,
                                                parent: "accounting"
                                            },
                                            list: "/chartofaccounts",
                                            create: "/chartofaccounts/create",
                                            edit: "/chartofaccounts/edit/:id",
                                            show: "/chartofaccounts/show/:id",
                                        },
                                        {
                                            name: "journalentries",
                                            meta: {
                                                label: "Journal Entries",
                                                icon: <FileTextOutlined />,
                                                parent: "accounting"
                                            },
                                            list: "/journalentries",
                                            create: "/journalentries/create",
                                            show: "/journalentries/show/:id",
                                        },
                                        {
                                            name: "accounting-periods",
                                            meta: {
                                                label: "Accounting Periods",
                                                icon: <CalculatorOutlined />,
                                                parent: "accounting"
                                            },
                                            list: "/accounting-periods",
                                            create: "/accounting-periods/create",
                                            edit: "/accounting-periods/edit/:id",
                                            show: "/accounting-periods/show/:id",
                                        },
                                        {
                                            name: "period-balances",
                                            meta: {
                                                label: "Period Balances",
                                                icon: <BarChartOutlined />,
                                                parent: "accounting"
                                            },
                                            list: "/period-balances",
                                            show: "/period-balances/show/:id",
                                        },
                                        {
                                            name: "financial-reports",
                                            list: "/financial-reports",
                                            meta: {
                                                label: "Financial Reports",
                                                icon: <BarChartOutlined />,
                                                parent: "accounting"
                                            },
                                        },
                                        {
                                            name: "vat-reports",
                                            list: "/vat-reports",
                                            meta: {
                                                label: "VAT Reports",
                                                icon: <BarChartOutlined />,
                                                parent: "accounting"
                                            },
                                        },
                                        {
                                            name: "reports",
                                            list: "/reports",
                                            meta: {
                                                icon: <BarChartOutlined />,
                                            },
                                        },
                                        {
                                            name: "prices",
                                            meta: {
                                                label: "Prices",
                                                icon: <TagOutlined />
                                            },
                                            list: "/prices",
                                            create: "/prices/create",
                                            edit: "/prices/edit/:id",
                                            show: "/prices/show/:id",
                                        },
                                        {
                                            name: "fiscalisation",
                                            list: "/fiscalisation",
                                            meta: {
                                                label: "Fiscal Device",
                                                icon: <BankOutlined />
                                            }
                                        },
                                        {
                                            name: "logout",
                                            list: "/logout",
                                            meta: {
                                                label: "Logout",
                                                icon: <LogoutOutlined />
                                            }
                                        },
                                    ]}
                                >
                                    <Routes>
                                        <Route path="/login" element={<Login />} />
                                        <Route element={<ProtectedRoute />}>
                                            <Route
                                                element={
                                                    <CustomLayout>
                                                        <Outlet />
                                                    </CustomLayout>
                                                }
                                            >
                                                <Route
                                                    index
                                                    element={<NavigateToResource resource="dashboard" />}
                                                />
                                                <Route path="/dashboard" element={<Dashboard />} />
                                                <Route path="/products">
                                                    <Route index element={<ProductList />} />
                                                    <Route path="create" element={<ProductCreate />} />
                                                    <Route path="edit/:id" element={<ProductEdit />} />
                                                    <Route path="show/:id" element={<ProductShow />} />
                                                </Route>
                                                <Route path="/stock">
                                                    <Route index element={<StockList />} />
                                                    <Route path="alerts" element={<StockAlerts />} />
                                                    <Route path="adjustments" element={<StockAdjustments />} />
                                                </Route>
                                                <Route path="/inventory">
                                                    <Route index element={<InventoryList />} />
                                                    <Route path="create" element={<InventoryCreate />} />
                                                    <Route path="edit/:id" element={<InventoryEdit />} />
                                                    <Route path="show/:id" element={<InventoryShow />} />
                                                </Route>
                                                <Route path="/purchases">
                                                    <Route index element={<PurchaseList />} />
                                                    <Route path="create" element={<PurchaseCreate />} />
                                                    <Route path="show/:id" element={<PurchaseShow />} />
                                                </Route>
                                                <Route path="/purchaseorders">
                                                    <Route index element={<PurchaseOrderList />} />
                                                    <Route path="create" element={<PurchaseOrderCreate />} />
                                                    <Route path="show/:id" element={<PurchaseOrderShow />} />
                                                </Route>

                                                <Route path="/sale-orders">
                                                    <Route index element={<SaleOrderList />} />
                                                    <Route path="create" element={<SaleOrderCreate />} />
                                                    <Route path="edit/:id" element={<SaleOrderEdit />} />
                                                    <Route path="show/:id" element={<SaleOrderShow />} />
                                                </Route>
                                                <Route path="/quotations">
                                                    <Route index element={<QuotationList />} />
                                                    <Route path="create" element={<QuotationCreate />} />
                                                    <Route path="edit/:id" element={<QuotationEdit />} />
                                                    <Route path="show/:id" element={<QuotationShow />} />
                                                </Route>
                                                <Route path="/sales">
                                                    <Route index element={<SaleList />} />
                                                    <Route path="create" element={<SaleCreate />} />
                                                    <Route path="edit/:id" element={<SaleEdit />} />
                                                    <Route path="show/:id" element={<SaleShow />} />
                                                </Route>
                                                <Route path="/customers">
                                                    <Route index element={<CustomerList />} />
                                                    <Route path="create" element={<CustomerCreate />} />
                                                    <Route path="edit/:id" element={<CustomerEdit />} />
                                                    <Route path="show/:id" element={<CustomerShow />} />
                                                </Route>
                                                <Route path="/currency">
                                                    <Route index element={<CurrencyList />} />
                                                    <Route path="create" element={<CurrencyCreate />} />
                                                    <Route path="edit/:id" element={<CurrencyEdit />} />
                                                    <Route path="show/:id" element={<CurrencyShow />} />
                                                </Route>
                                                <Route path="/users">
                                                    <Route index element={<UserList />} />
                                                    <Route path="create" element={<UserCreate />} />
                                                    <Route path="edit/:id" element={<UserEdit />} />
                                                </Route>
                                                <Route path="/vendors">
                                                    <Route index element={<VendorList />} />
                                                    <Route path="create" element={<VendorCreate />} />
                                                    <Route path="edit/:id" element={<VendorEdit />} />
                                                    <Route path="show/:id" element={<VendorShow />} />
                                                </Route>
                                                <Route path="/salereturns">
                                                    <Route index element={<SaleReturnList />} />
                                                    <Route path="create" element={<SaleReturnCreate />} />
                                                    <Route path="show/:id" element={<SaleReturnShow />} />
                                                </Route>
                                                <Route path="/reports" element={<Reports />} />
                                                <Route path="/prices">
                                                    <Route index element={<PriceListList />} />
                                                    <Route path="create" element={<PriceListCreate />} />
                                                    <Route path="edit/:id" element={<PriceListEdit />} />
                                                    <Route path="show/:id" element={<PriceListShow />} />
                                                </Route>
                                                <Route path="/fiscalisation">
                                                    <Route index element={<FiscalisationDashboard />} />
                                                </Route>
                                                <Route path="/branch">
                                                    <Route index element={<BranchList />} />
                                                    <Route path="create" element={<BranchCreate />} />
                                                    <Route path="edit/:id" element={<BranchEdit />} />
                                                    <Route path="show/:id" element={<BranchShow />} />
                                                </Route>
                                                <Route path="/taxes">
                                                    <Route index element={<TaxesList />} />
                                                    <Route path="create" element={<TaxesCreate />} />
                                                    <Route path="edit/:id" element={<TaxesEdit />} />
                                                    <Route path="show/:id" element={<TaxesShow />} />
                                                </Route>
                                                <Route path="/company">
                                                    <Route index element={<CompanyList />} />
                                                    <Route path="edit/:id" element={<CompanyEdit />} />
                                                    <Route path="show/:id" element={<CompanyShow />} />
                                                </Route>
                                                <Route path="/chartofaccounts">
                                                    <Route index element={<ChartOfAccountsList />} />
                                                    <Route path="create" element={<ChartOfAccountsCreate />} />
                                                    <Route path="show/:id" element={<ChartOfAccountsShow />} />
                                                </Route>
                                                <Route path="/journalentries">
                                                    <Route index element={<JournalEntriesList />} />
                                                    <Route path="create" element={<JournalEntriesCreate />} />
                                                    <Route path="show/:id" element={<JournalEntriesShow />} />
                                                </Route>
                                                <Route path="/accounting-periods">
                                                    <Route index element={<AccountingPeriodsList />} />
                                                    <Route path="create" element={<AccountingPeriodsCreate />} />
                                                    <Route path="edit/:id" element={<AccountingPeriodsEdit />} />
                                                    <Route path="show/:id" element={<AccountingPeriodsShow />} />
                                                </Route>
                                                <Route path="/period-balances">
                                                    <Route index element={<PeriodBalancesList />} />
                                                    <Route path="show/:id" element={<PeriodBalancesShow />} />
                                                </Route>
                                                <Route path="/financial-reports" element={<FinancialReports />} />
                                                <Route path="/vat-reports" element={<VatReport />} />
                                                <Route path="/logout" element={<Logout />} />
                                            </Route>
                                        </Route>
                                        <Route path="*" element={<ErrorComponent />} />
                                    </Routes>
                                    <RefineKbar />
                                    <UnsavedChangesNotifier />
                                </Refine>
                            </ErrorBoundary>
                        </AntdApp>
                    </ConfigProvider>
                </RefineKbarProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App; 